/** Browser cache that belongs to one logged-in account — not shared. */
const KEEP_KEYS = new Set(["token", "user", "ap_login_cache", "ap_signup_draft"]);

const OWNER_KEY = "ap_workspaceOwner";
const LAST_OWNER_KEY = "ap_lastOwner";
const FRESH_KEY = "ap_freshWorkspace";

function userKey(user) {
  if (!user) return "";
  const id = user.id || user._id || "";
  const email = String(user.email || "").trim().toLowerCase();
  return String(id || email || "").trim();
}

function currentOwner() {
  try {
    return String(localStorage.getItem(OWNER_KEY) || "").trim();
  } catch {
    return "";
  }
}

function lastOwner() {
  try {
    return String(sessionStorage.getItem(LAST_OWNER_KEY) || "").trim();
  } catch {
    return "";
  }
}

function shouldWipeKey(k) {
  if (!k || KEEP_KEYS.has(k)) return false;
  if (k === OWNER_KEY) return true;
  return (
    k.startsWith("ap_") ||
    k === "ps_draft" ||
    k === "qbank_draft" ||
    k === "facultyActivityHistory"
  );
}

/**
 * Wipe planner drafts, plans, wizard fields, and related caches.
 * Keeps login remember-me. Optionally remember who just logged out.
 */
export function clearWorkspaceCache({ rememberOwner = false } = {}) {
  try {
    const owner = rememberOwner ? currentOwner() : "";
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k) keys.push(k);
    }
    keys.forEach((k) => {
      if (shouldWipeKey(k)) localStorage.removeItem(k);
    });
    if (rememberOwner && owner) sessionStorage.setItem(LAST_OWNER_KEY, owner);
    else sessionStorage.removeItem(LAST_OWNER_KEY);
  } catch (_) {
    /* ignore quota / private mode */
  }
}

/**
 * If this is a different email/id than the last session, drop leftover
 * wizard + plans so the new account starts empty.
 */
export function adoptWorkspaceUser(user) {
  const next = userKey(user);
  const prev = currentOwner() || lastOwner();
  if (next && prev && prev === next) {
    try {
      sessionStorage.removeItem(LAST_OWNER_KEY);
    } catch (_) {
      /* ignore */
    }
    try {
      if (next) localStorage.setItem(OWNER_KEY, next);
    } catch (_) {
      /* ignore */
    }
    return false;
  }
  const accountChanged = Boolean(prev && next && prev !== next);
  const firstSession = Boolean(next && !prev);
  clearWorkspaceCache({ rememberOwner: false });
  try {
    sessionStorage.removeItem(LAST_OWNER_KEY);
    if (accountChanged || firstSession) sessionStorage.setItem(FRESH_KEY, "1");
    if (next) localStorage.setItem(OWNER_KEY, next);
  } catch (_) {
    /* ignore */
  }
  return accountChanged || firstSession;
}

/** True once after switching to a different email/id. */
export function consumeFreshWorkspace() {
  try {
    const v = sessionStorage.getItem(FRESH_KEY) === "1";
    sessionStorage.removeItem(FRESH_KEY);
    return v;
  } catch {
    return false;
  }
}

export function workspaceOwnerKey(user) {
  return userKey(user);
}
